import { DollarOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Col, Form, message, Popconfirm, Row, Space, Table, Tag, Typography } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { selectUser } from '../../../../auth/states/userSlice';
import { IAllInvoices } from '../../../../common/types/commonInterfaces';
import { IPermission } from '../../../../common/types/commonTypes';
import { debounce, formatAmount, separateClient } from '../../../../common/utils/common.utils';
import usePagination from '../../../../common/utils/usePagination';
import useUrlParams from '../../../../common/utils/useUrlParams';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { DateInput, FormInput } from '../../../../components/common/FormItem/FormItems';
import Reload from '../../../../components/common/reload/Reload';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import { useVisaStatusUpdateMutation } from '../../../../components/notificatioin/Api/NotificationEndpoints';
import { formatDateRangeArray, parseDateRangeObject } from '../../../../helpers/healper';
import InvoiceOtherAddMoneyModal from '../../../Invoice(Other)/Modals/InvoiceOtherAddMoneyModal';
import {
  useDeleteInvoiceVisaMutation,
  useGetAllInvoiceVisaV2Query,
} from '../../Api/Endpoints/invoiceVisaEndpoints';
import VisaApproveModal from '../../Modals/VisaApproveModal';
type Props = {
  permission?: IPermission;
  addMRPermission: ['*'] | undefined;
};

const InvoiceVisaLists = ({ permission, addMRPermission }: Props) => {
  const { setUrlParam, setUrlParamsFromObject, getAllUrlParams } = useUrlParams();
  const { page, size, changePagination, pagination } = usePagination();
  const params_value = getAllUrlParams() as {
    search: string | null;
    from_date: string | null;
    to_date: string | null;
  };
  const [columnIndex, setColumnIndex] = useState<number | null>(null);
  const user = useSelector(selectUser);

  const [fetchVisaStatus] = useVisaStatusUpdateMutation();

  // LINKS
  const createLink = '/visa/add';
  const viewLink = '/visa/details';
  const editLink = '/visa/edit';

  // GET ALL DATA ,TRASH, DELETE AND RESTORE
  const {
    data,
    isFetching,
    refetch,
    isLoading: dataIsLoading,
  } = useGetAllInvoiceVisaV2Query({
    ...getAllUrlParams(),
    page,
    size,
  });

  const [deleteInvoice, { isError, isSuccess, isLoading: deleteLoading, error }] =
    useDeleteInvoiceVisaMutation();

  // DELETE HANDLER
  const handleOnDelete = (id: number, index: number) => {
    setColumnIndex(index);
    deleteInvoice({
      id,
      user_id: user?.user_id as number,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      setColumnIndex(null);
      message.success('Invoice Deleted Successfully!');
    }
    if (isError) {
      setColumnIndex(null);
      message.error('Thare was an error');
    }
  }, [isSuccess, isError]);

  const columns: ColumnsType<IAllInvoices> = [
    {
      title: 'SL.',
      render: (_, data, index) => (
        <>{((pagination?.current || 1) - 1) * (pagination?.pageSize || 100) + 1 + index}</>
      ),
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice_no',
      key: 'invoice_no',
      align: 'left',
    },
    {
      title: 'Sales Date',
      key: 'invoice_date',
      align: 'center',
      render: (_, data) => <>{moment(data.invoice_date).format('DD MMM YYYY')} </>,
      sorter: (a, b) => moment(a.invoice_date).unix() - moment(b.invoice_date).unix(),
      width: 120,
    },

    {
      title: 'Client Name',
      dataIndex: 'client_name',
      key: 'client_name',
      render: (value, record) => {
        const { client_id, combined_id } = separateClient(record?.comb_client);

        return (
          <Link
            to={
              client_id
                ? `/clients/details/${record?.comb_client}`
                : `/combineClients/details/${combined_id}`
            }
          >
            <span>
              {record.client_name}
              {record.mobile ? ' ' + record.mobile : ''}
            </span>
          </Link>
        );
      },
      width: 150,
    },

    {
      title: 'PP No / Name',
      dataIndex: 'pp_or_name',
      key: 'pp_or_name',
      width: 130,
    },
    {
      title: 'Sales Price',
      dataIndex: 'net_total',
      key: 'net_toal',
      width: 120,
      align: 'right',
      sorter: (a, b) => Number(a.net_total) - Number(b.net_total),

      render: (curr) => <span className='color-sales'>{formatAmount(curr)}</span>,
    },
    {
      title: 'Rec Amount',
      dataIndex: 'invclientpayment_amount',
      key: 'invclientpayment_amount',
      align: 'right',
      width: 120,
      render: (curr) => <span className='color-collection'>{formatAmount(curr)}</span>,
    },
    {
      title: 'Due Amount',
      align: 'right',
      width: 120,
      render(_, record) {
        const dueAmount = Number(record?.net_total) - Number(record?.invclientpayment_amount);

        return dueAmount > 0 ? (
          <span style={{ color: 'red' }}>
            {formatAmount(Number(record?.net_total) - Number(record?.invclientpayment_amount))}
          </span>
        ) : (
          <Tag color='cyan' icon={<DollarOutlined />}>
            PAID
          </Tag>
        );
      },
      sorter: (a, b) => Number(a.net_total) - Number(b.net_total),
    },
    {
      title: 'MR. No.',
      dataIndex: 'money_receipt_num',
      key: 'money_receipt_num',
      width: 120,
      render: (_, data) => (
        <span>
          {data?.money_receipt_num?.split(',').map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </span>
      ),
    },
    {
      title: 'Sales By',
      dataIndex: 'sales_by',
      key: 'sales_by',
      width: 120,
    },
    {
      title: 'Status',
      dataIndex: 'billing_status',
      key: 'billing_status',
      align: 'center',
      width: '100px',
      render: (_, data) => <VisaApproveModal id={data.invoice_id} />,
    },
    {
      title: 'Action',
      key: 'operation',
      width: 180,
      fixed: 'right',
      render: (_, data, index) => (
        <Space size='small'>
          {permission?.['read:any'] && (
            <Link to={viewLink + '/' + data.invoice_id} state={location.pathname}>
              <Button size='small' type='primary'>
                <Typography style={{ color: '#ffffff' }}>View</Typography>
              </Button>
            </Link>
          )}
          {permission?.['update:any'] && !Number(data?.invclientpayment_amount || 0) && (
            <Link to={editLink + '/' + data.invoice_id} state={location.pathname}>
              <Button size='small' type='primary'>
                Edit
              </Button>
            </Link>
          )}

          {permission?.['delete:any'] &&
            // data.billing_status !== 'Pending' &&
            data.billing_status !== 'Approved' && (
              <Button size='small' type='primary' danger loading={columnIndex === index && true}>
                <Popconfirm
                  onConfirm={() => handleOnDelete(data.invoice_id, index)}
                  title='Sure to delete?'
                >
                  Delete
                </Popconfirm>
              </Button>
            )}

          {/* add money receipt */}
          {addMRPermission && data.approve_amount > 0 && (
            <InvoiceOtherAddMoneyModal
              InvocieModalTitle={'Invoice Visa'}
              invoice_id={data.invoice_id}
              disabled={Number(data.approve_amount) === Number(data.invclientpayment_amount)}
            />
          )}
        </Space>
      ),
    },
  ];

  const dataWithKey = data?.data?.map((item, index) => ({
    key: String(index),
    ...item,
  }));
  const [form] = useForm();

  const handelSearch = useCallback(
    debounce((e: string) => {
      setUrlParam('search', e);
    }, 600),
    []
  );

  useEffect(() => {
    form.setFieldsValue({
      search: params_value.search,
      date_range: parseDateRangeObject({
        from_date: params_value.from_date,
        to_date: params_value.to_date,
      }),
    });
  }, [params_value]);

  return (
    <>
      {/* BREADCRUMB */}
      <BreadCrumb arrOfOption={['Invoice', 'Visa']} />

      {/* HEADER */}
      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 10 }} justify={'space-between'}>
        <Col span={12}>
          <Row gutter={10}>
            {permission?.['create:any'] && (
              <Col xs={24} sm={12} md={8} lg={8}>
                <Link to={createLink}>
                  <Button type='primary' icon={<PlusOutlined />} style={{ width: '100%' }}>
                    Create
                  </Button>
                </Link>
              </Col>
            )}

            <Col xs={24} sm={12} md={8} lg={8} style={{ display: 'flex' }}>
              <Col>
                <Reload
                  refetch={() => {
                    refetch();
                  }}
                />
              </Col>
            </Col>
          </Row>
        </Col>

        <Col span={12}>
          <Form form={form}>
            <Row justify={'end'} gutter={[10, 10]}>
              <DateInput
                name={'date_range'}
                rangePicker
                size={8}
                noStyle
                onChange={(e: any) => setUrlParamsFromObject(formatDateRangeArray(e))}
              />
              <FormInput
                name={'search'}
                placeholder='Search here ...'
                onChange={(e: string) => handelSearch(e.trim())}
              />
            </Row>
          </Form>
        </Col>
      </Row>

      {/* TABLE */}
      <div>
        <span style={{ float: 'left', marginBottom: '1rem' }}>
          You have total <strong>{data?.count ? data?.count : data?.data?.length}</strong> rows
        </span>
        <Table
          loading={{
            spinning: dataIsLoading || isFetching,
            indicator: loadingIndicator,
          }}
          size='small'
          bordered
          rowKey={(e) => e.invoice_id}
          columns={columns}
          dataSource={dataWithKey}
          scroll={{ x: 'max-content' }}
          pagination={changePagination(data?.count)}
        />
      </div>
    </>
  );
};

export default InvoiceVisaLists;
